import { Link } from "react-router";
import { RouteEnum } from "../../../common/enums/RouteEnum";
import {
    setAchievedFilter,
    setCurrentPage,
    setFunctionGroupFilter,
    setPrefillFilter,
    setQualificationStandardFilter,
    setSearchTerm,
} from "../QualificationListSlice";
import {
    InputField,
    SelectField,
} from "@hatch-digital-nl/abs-storybook/legacy";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useGetQualificationsQuery } from "../../../common/api/qualification";
import Skeleton from "react-loading-skeleton";
import searchImage from "../../../common/images/search.svg";
import { SelectFieldType } from "../../../common/types/Types";
import { useGetFunctionGroupsQuery } from "../../../common/api/functionGroup";
import { getFunctionGroupOptions } from "../utils/getFunctionGroupOptions";
import { getQualificationStandardOptions } from "../utils/getQualificationStandardOptions";
import { debounce } from "../../../common/utils/debounce";
import { JSX, useEffect } from "react";
import { useGetQualificationStandardsQuery } from "../../../common/api/qualificationStandard";
import { useIsEstablishmentManager } from "../../../common/utils/useIsEstablishmentManager";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { useEmployeeId } from "../../../common/utils/useEmployeeId";
import { getAchievedFilter } from "../../../common/utils/getAchievedFilter";

const QualificationListHeader = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const establishmentId = useEstablishmentId();
    const employeeId = useEmployeeId();
    const isEstablishmentManager = useIsEstablishmentManager() ?? false;
    const {
        currentPage,
        searchTerm,
        itemsPerPage,
        functionGroupFilter,
        qualificationStandardFilter,
        achievedFilter,
        sort,
        prefillFilter,
    } = useAppSelector((state) => state.qualificationList);
    const { data: functionGroupData } = useGetFunctionGroupsQuery({
        items_per_page: 9999,
    });
    const { data: qualificationStandardData } =
        useGetQualificationStandardsQuery({ items_per_page: 9999 });

    const { data: qualificationsData, isLoading } = useGetQualificationsQuery({
        page: currentPage,
        items_per_page: itemsPerPage,
        sort: sort.sortName,
        filter: {
            search: searchTerm,
            ...(qualificationStandardFilter
                ? {
                      qualification_standard_id:
                          qualificationStandardFilter.value,
                  }
                : {}),
            ...(functionGroupFilter
                ? { function_group_id: functionGroupFilter.value }
                : {}),
            ...getAchievedFilter({
                achievedFilter,
                isEstablishmentManager,
                establishmentId,
                employeeId,
            }),
            ...(isEstablishmentManager && establishmentId
                ? { establishment_id: establishmentId }
                : {}),
        },
    });

    useEffect(() => {
        if (!qualificationStandardData || !prefillFilter) return;

        const match = qualificationStandardData.data.find(
            (q) => q.code === prefillFilter,
        );

        if (match) {
            dispatch(
                setQualificationStandardFilter({
                    value: match.id.toString(),
                    label: match.tag,
                }),
            );
        }

        dispatch(setPrefillFilter(null));
    }, [qualificationStandardData, prefillFilter, dispatch]);

    if (isLoading) {
        return <Skeleton height={"5rem"} count={2} borderRadius={"1.5rem"} />;
    }

    return (
        <section className="p-qualification-list__header">
            <div className="c-breadcrumbs">
                <span>
                    Dashboards / <Link to={RouteEnum.DASHBOARD}>LMS</Link> /{" "}
                    <Link to={RouteEnum.QUALIFICATIONS}>Kwalificaties</Link>
                </span>
            </div>
            <div className="c-header-search">
                <div className="c-header-search__title">
                    <h2>Kwalificaties ({qualificationsData?.meta?.total})</h2>
                </div>
                <div className="c-header-search__search c-header-search__search--filters">
                    <SelectField
                        placeHolder={"Filter op behaald"}
                        options={[
                            { value: "1", label: "Behaald" },
                            { value: "0", label: "Niet behaald" },
                        ]}
                        onChange={(value: SelectFieldType) =>
                            dispatch(setAchievedFilter(value))
                        }
                        value={achievedFilter}
                        isClearable={true}
                        isSearchable={false}
                    />
                    <SelectField
                        placeHolder={"Filter op norm"}
                        options={getQualificationStandardOptions(
                            qualificationStandardData?.data,
                        )}
                        onChange={(value: SelectFieldType) =>
                            dispatch(setQualificationStandardFilter(value))
                        }
                        value={qualificationStandardFilter}
                        isClearable={true}
                        isSearchable={false}
                    />
                    <SelectField
                        placeHolder={"Filter op functiegroep"}
                        options={getFunctionGroupOptions(
                            functionGroupData?.data,
                        )}
                        onChange={(value: SelectFieldType) =>
                            dispatch(setFunctionGroupFilter(value))
                        }
                        value={functionGroupFilter}
                        isClearable={true}
                        isSearchable={false}
                    />
                    <InputField
                        placeHolder={"Zoeken..."}
                        iconRight={searchImage}
                        onChange={debounce((value: string) => {
                            dispatch(setSearchTerm(value));
                            dispatch(setCurrentPage(1));
                        })}
                    />
                </div>
            </div>
        </section>
    );
};

export default QualificationListHeader;
